import dxDataGrid from "devextreme/ui/data_grid";
import { getUserScopedDXGridStorageKey } from "../services/CookieService";
import GridLayoutService from "../services/GridLayoutService";

let hasInitializedGridLayoutPersistence = false;
const configuredGridInstances = new WeakSet<any>();
// Grids currently having a saved state applied programmatically. Used to ignore
// the optionChanged events that our own state() calls trigger, so we don't
// re-save (and loop) when restoring a layout.
const applyingGridInstances = new WeakSet<any>();
// Grids that have finished their initial render + a short settle window and are
// now allowed to persist changes. DevExtreme emits `columns` optionChanged
// events (auto column widths, column generation) during load that are NOT user
// actions; without this gate they would fire a POST on page load.
const saveReadyGrids = new WeakSet<any>();
// Last state we persisted/applied per grid, serialized, to dedupe redundant
// saves (e.g. re-applying server state, or repeated identical option churn).
const lastPersistedState = new WeakMap<any, string>();

// Time at which a grid finished reconciling with the backend (GET resolved or
// failed). Saves are blocked until then so a default/legacy layout produced
// during load can never overwrite the user's DB layout (e.g. after clearing
// browser cookies/site data, when there is no local cache to restore from).
const gridReconciledAt = new WeakMap<any, number>();
// Timestamp of the last genuine user input anywhere in the document. Layout
// changes are only persisted when they closely follow real user interaction,
// so DevExtreme's own data-refresh/auto-width churn is never saved.
let lastUserInputAt = 0;
const USER_INTENT_WINDOW_MS = 2500;
// Number of live grids per page, used to invalidate the page GET memo and flush
// pending saves once the user leaves the page.
const pageGridCounts = new Map<string, number>();
// In-flight POST per page; a subsequent GET for the page waits for it so it
// never reads a layout older than the one just saved.
const pageSaveInFlight = new Map<string, Promise<void>>();
// Layout the grid SHOULD show (last restored from cache/DB or saved by the
// user). Used to re-apply it when the host replaces the whole `columns` array
// (e.g. a React re-render after async data arrives), which makes DevExtreme
// rebuild columns from config and silently drop the restored layout.
const desiredGridState = new WeakMap<any, any>();

// One in-flight/cached GET per page, so multiple grids on the same page share a
// single network call. Entry is cleared shortly after resolution so a later
// navigation to the page fetches fresh data.
const pageLayoutRequests = new Map<string, Promise<Map<string, any>>>();

// Pending save batch per page: collects changed grids and flushes them together
// in a single POST.
interface PageSaveBuffer {
    grids: Map<string, PendingGridSave>;
    timer: ReturnType<typeof setTimeout> | null;
}
interface PendingGridSave {
    viewKey?: string;
    state: any;
}
const pageSaveBuffers = new Map<string, PageSaveBuffer>();

const gridLayoutService = new GridLayoutService();

// optionChanged fullNames that represent a persisted layout change (column
// order/width/visibility, sorting, grouping, filtering, paging, search).
const LAYOUT_OPTION_PATTERN = /^(columns|filterValue|filterPanel|paging|searchPanel|selectionFilter|sorting|columnChooser|headerFilter)/;

const SAVE_DEBOUNCE_MS = 600;

// DevExtreme raises `optionChanged` for a column change BEFORE it updates the
// state returned by `component.state()` (that happens later, in the columns
// controller's endUpdate -> columnsChanged). Reading state() synchronously
// therefore returns the PREVIOUS layout. Capture it shortly afterwards instead.
const STATE_CAPTURE_DELAY_MS = 50;
// Pending deferred state capture per grid, so it can be run immediately if the
// grid is disposed (user navigates away) before the timer fires.
const pendingStateCaptures = new WeakMap<any, { timer: ReturnType<typeof setTimeout>; run: () => void }>();
// Ignore layout churn for a short settle window after the grid's first render,
// so DevExtreme's load-time column sizing/generation does not trigger a save.
const SAVE_READY_DELAY_MS = 400;
// Fallback in case `contentReady` fired before we subscribed to it.
const SAVE_READY_FALLBACK_MS = 1500;

interface GridIdentity {
    pageKey: string;
    gridKey: string;
    viewKey: string;
    localCacheKey: string;
}

/**
 * Produces a stable hash for deriving compact, deterministic grid keys.
 */
const hashString = (value: string): string => {
    let hash = 0;

    for (let i = 0; i < value.length; i++) {
        hash = (hash << 5) - hash + value.charCodeAt(i);
        hash |= 0;
    }

    return Math.abs(hash).toString(36);
};

/**
 * Returns the declared StateStoring storageKey if the grid provides one.
 */
const getDeclaredStorageKey = (component: any): string => {
    const stateStoring = component?.option?.("stateStoring");
    const storageKey = stateStoring?.storageKey;

    return typeof storageKey === "string" && storageKey.length > 0 ? storageKey : "";
};

/**
 * Builds a deterministic grid key for grids that do not declare a storageKey.
 *
 * Uses the page path, the element id (when present) and the set of column
 * fields. This stays stable across SPA navigations, so a grid keeps the same
 * key every time the page is visited.
 */
const buildFallbackGridKey = (component: any, pageKey: string): string => {
    const element = component?.element?.();
    const elementId = element?.id || component?.option?.("elementAttr")?.id || "";
    const columns = component?.option?.("columns") || [];
    const columnSignature = columns
        .map((column: any) => column?.name || column?.dataField || column?.caption || "col")
        .sort()
        .join("|");

    return `grid_${hashString(`${pageKey}|${elementId}|${columnSignature}`)}`;
};

/**
 * Canonical page key: the same route reached via different links (e.g.
 * "/manageviewshift" from the menu vs "/manageviewshift/?clientId=" from Home /
 * Schedule) must map to ONE key, otherwise layouts are saved under one key and
 * looked up under another. React Router matches these case- and
 * trailing-slash-insensitively, so normalize the same way.
 */
const normalizePageKey = (pathname: string): string => {
    const trimmed = (pathname || "/").toLowerCase().replace(/\/+$/, "");
    return trimmed.length > 0 ? trimmed : "/";
};

const isDevelopment = process.env.NODE_ENV !== "production";

/**
 * Development-only diagnostics for layout persistence (identity, GET/POST
 * results). Never logs grid row data, only layout keys.
 */
const debugLog = (message: string, details?: any): void => {
    if (isDevelopment) {
         
        console.info(`[GridLayout] ${message}`, details ?? "");
    }
};

/**
 * Reports a persistence API failure to the console and App Insights instead of
 * swallowing it silently, so a broken save/load round-trip is visible.
 */
const reportApiFailure = (operation: string, pageKey: string, error: any): void => {
    const status = error?.response?.status ?? "network";
    const message = `[GridLayout] ${operation} failed for page "${pageKey}" (status: ${status})`;
     
    console.warn(message, error?.response?.data ?? error);
    try {
        gridLayoutService.traceAsErrorToAppInsights(message);
    } catch {
        // Telemetry must never break the page.
    }
};

/**
 * Resolves the page/grid identity used for both the backend API and the
 * per-user offline localStorage cache.
 */
const resolveGridIdentity = (component: any): GridIdentity => {
    const pageKey = normalizePageKey(typeof window !== "undefined" ? window.location.pathname : "");
    const declaredKey = getDeclaredStorageKey(component);
    const gridKey = declaredKey || buildFallbackGridKey(component, pageKey);

    return {
        pageKey,
        gridKey,
        viewKey: "default",
        localCacheKey: getUserScopedDXGridStorageKey(gridKey),
    };
};

/**
 * Treats only a state object that actually carries layout information as
 * meaningful, so we never apply an empty `{}` that could blank a grid.
 */
const isMeaningfulState = (state: any): boolean => {
    return !!state && typeof state === "object" && Object.keys(state).length > 0;
};

const readLocalCache = (localCacheKey: string): any => {
    try {
        const raw = localStorage.getItem(localCacheKey);
        const parsed = raw && raw.length > 0 ? JSON.parse(raw) : null;
        return isMeaningfulState(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const writeLocalCache = (localCacheKey: string, state: any): void => {
    try {
        localStorage.setItem(localCacheKey, JSON.stringify(state));
    } catch {
        // Ignore storage quota / serialization failures; the API remains the
        // source of truth.
    }
};

/**
 * Applies a saved state to the grid, guarding against the re-save loop that our
 * own state() call would otherwise trigger via optionChanged.
 */
const applyGridState = (component: any, state: any): void => {
    if (!isMeaningfulState(state)) {
        return;
    }

    applyingGridInstances.add(component);

    try {
        component.state(state);
        desiredGridState.set(component, state);
    } catch {
        // Ignore invalid state payloads; keep the current layout.
    } finally {
        // Release the guard after the synchronous change events have settled,
        // and baseline from the grid's OWN serialized state so identical
        // follow-up churn is deduped (the raw input serializes differently).
        setTimeout(() => {
            applyingGridInstances.delete(component);
            try {
                lastPersistedState.set(component, JSON.stringify(component.state()));
            } catch {
                // Grid disposed; nothing to baseline.
            }
        }, 0);
    }
};

/**
 * Normalizes a server-stored layout: the backend may return the opaque state
 * either as an object or as a JSON string. Invalid payloads yield null.
 */
const parseServerState = (state: any): any => {
    if (typeof state !== "string") {
        return state ?? null;
    }

    try {
        return JSON.parse(state);
    } catch {
        return null;
    }
};

/**
 * Re-applies the desired layout after the host replaced the whole `columns`
 * option (DevExtreme rebuilds columns from config and drops user state).
 * Deferred so the new columns are initialized first; skipped when the grid
 * already matches, which also prevents any apply/replace loop.
 */
const reapplyDesiredStateAfterColumnsReset = (component: any): void => {
    setTimeout(() => {
        const desired = desiredGridState.get(component);
        if (!isMeaningfulState(desired) || !component.element?.()) {
            return;
        }

        try {
            if (JSON.stringify(component.state()) === JSON.stringify(desired)) {
                return;
            }
        } catch {
            return;
        }

        debugLog("columns option replaced by host; re-applying layout");
        applyGridState(component, desired);
    }, 0);
};

/**
 * Loads every grid layout for a page in a SINGLE GET call, memoized so all grids
 * on the page share one request. Returns a map of gridKey -> state.
 */
const loadPageLayouts = (pageKey: string): Promise<Map<string, any>> => {
    // Navigation guard: if we still hold memoized results for a DIFFERENT page,
    // the user has navigated away from it, so drop those entries. This keeps the
    // current page's GET memoized for the WHOLE visit (so every grid shares one
    // call no matter when it mounts), while still refetching fresh data when the
    // page is revisited.
    for (const cachedPageKey of Array.from(pageLayoutRequests.keys())) {
        if (cachedPageKey !== pageKey) {
            pageLayoutRequests.delete(cachedPageKey);
        }
    }

    const existing = pageLayoutRequests.get(pageKey);
    if (existing) {
        return existing;
    }

    // Wait for any in-flight save for this page so the GET returns fresh data.
    const pendingSave = pageSaveInFlight.get(pageKey) ?? Promise.resolve();

    const request = pendingSave
        .then(() => gridLayoutService.getPageLayouts(pageKey))
        .then((response: any) => {
            const grids = response?.data?.data?.grids;
            const layoutByGridKey = new Map<string, any>();

            if (Array.isArray(grids)) {
                grids.forEach((grid: any) => {
                    const state = parseServerState(grid?.layout?.state);
                    if (grid?.gridKey && isMeaningfulState(state)) {
                        layoutByGridKey.set(grid.gridKey, state);
                    }
                });
            }

            debugLog(`GET ${pageKey} -> gridKeys`, Array.from(layoutByGridKey.keys()));

            return layoutByGridKey;
        })
        .catch((error: any) => {
            reportApiFailure("GET", pageKey, error);
            return new Map<string, any>();
        });

    pageLayoutRequests.set(pageKey, request);

    return request;
};

/**
 * Flushes all buffered grid saves for a page in a single POST.
 */
const flushPageSaves = (pageKey: string): void => {
    const buffer = pageSaveBuffers.get(pageKey);
    if (!buffer || buffer.grids.size === 0) {
        return;
    }

    const entries = Array.from(buffer.grids.entries()).map(([gridKey, pending]) => ({
        gridKey,
        viewKey: pending.viewKey,
        state: pending.state,
    }));

    buffer.grids.clear();
    if (buffer.timer) {
        clearTimeout(buffer.timer);
    }
    buffer.timer = null;

    const saveRequest = gridLayoutService
        .savePageLayouts(pageKey, entries)
        .then((response: any) => {
            debugLog(`POST ${pageKey} (status ${response?.status}) gridKeys`, entries.map((entry) => entry.gridKey));
        })
        .catch((error: any) => {
            // Layouts remain cached locally per grid and will be re-sent on the
            // next change; surface the failure so it is not silently lost.
            reportApiFailure("POST", pageKey, error);
        })
        .finally(() => {
            if (pageSaveInFlight.get(pageKey) === saveRequest) {
                pageSaveInFlight.delete(pageKey);
            }
        });

    pageSaveInFlight.set(pageKey, saveRequest);
};

/**
 * Buffers one grid's latest state for its page and (re)schedules a single
 * debounced POST that saves every changed grid on the page together.
 */
const bufferPageSave = (identity: GridIdentity, state: any): void => {
    if (!isMeaningfulState(state)) {
        return;
    }

    // Update the per-grid offline cache immediately for instant restore.
    writeLocalCache(identity.localCacheKey, state);

    // Write through to the memoized page GET so a grid re-mounted during the
    // same visit restores the latest layout instead of the pre-change one.
    pageLayoutRequests.get(identity.pageKey)?.then((layoutByGridKey) => {
        layoutByGridKey.set(identity.gridKey, state);
    });

    let buffer = pageSaveBuffers.get(identity.pageKey);
    if (!buffer) {
        buffer = { grids: new Map(), timer: null };
        pageSaveBuffers.set(identity.pageKey, buffer);
    }

    buffer.grids.set(identity.gridKey, { viewKey: identity.viewKey, state });

    if (buffer.timer) {
        clearTimeout(buffer.timer);
    }
    buffer.timer = setTimeout(() => flushPageSaves(identity.pageKey), SAVE_DEBOUNCE_MS);
};

/**
 * True only when a layout change can be attributed to the user: the grid has
 * reconciled with the backend AND real pointer/keyboard input happened after
 * that, very recently. Prevents load/refresh churn from overwriting the DB.
 */
const isUserDrivenChange = (component: any): boolean => {
    const reconciledAt = gridReconciledAt.get(component);
    if (reconciledAt === undefined) {
        return false;
    }

    return lastUserInputAt >= reconciledAt && Date.now() - lastUserInputAt <= USER_INTENT_WINDOW_MS;
};

/**
 * Marks the grid as reconciled with the backend and records its current state
 * as the save baseline, so only subsequent real changes are persisted.
 */
const markGridReconciled = (component: any): void => {
    try {
        if (!lastPersistedState.has(component)) {
            lastPersistedState.set(component, JSON.stringify(component.state()));
        }
    } catch {
        // Grid may have been disposed; nothing to baseline.
    }

    gridReconciledAt.set(component, Date.now());
};

/**
 * Counts live grids per page. When the last grid on a page is disposed (user
 * navigated away), pending saves are flushed and the page GET memo dropped so
 * the next visit fetches the latest layout from the backend.
 */
const trackGridLifetime = (component: any, pageKey: string): void => {
    pageGridCounts.set(pageKey, (pageGridCounts.get(pageKey) ?? 0) + 1);

    component.on("disposing", () => {
        // Persist a change made just before navigating away.
        runPendingStateCapture(component);

        const remaining = (pageGridCounts.get(pageKey) ?? 1) - 1;

        if (remaining > 0) {
            pageGridCounts.set(pageKey, remaining);
            return;
        }

        pageGridCounts.delete(pageKey);
        flushPageSaves(pageKey);
        pageLayoutRequests.delete(pageKey);
    });
};

/**
 * Runs a pending deferred state capture immediately (e.g. on dispose).
 */
const runPendingStateCapture = (component: any): void => {
    const pending = pendingStateCaptures.get(component);
    if (!pending) {
        return;
    }

    clearTimeout(pending.timer);
    pendingStateCaptures.delete(component);
    pending.run();
};

/**
 * Schedules (coalesced per grid) a read of the grid's state AFTER DevExtreme has
 * finished applying the current change, then buffers it for saving if it
 * differs from what was last persisted.
 */
const scheduleStateCapture = (component: any, identity: GridIdentity, trigger: string): void => {
    const run = () => {
        let currentState: any;
        try {
            currentState = component.state();
        } catch {
            // Ignore transient state() read failures.
            return;
        }

        // Skip if nothing actually changed versus what we last saved/applied.
        const serialized = JSON.stringify(currentState);
        if (lastPersistedState.get(component) === serialized) {
            return;
        }
        lastPersistedState.set(component, serialized);
        desiredGridState.set(component, currentState);

        debugLog(`save queued ${identity.gridKey}`, { trigger });
        bufferPageSave(identity, currentState);
    };

    const existing = pendingStateCaptures.get(component);
    if (existing) {
        clearTimeout(existing.timer);
    }

    const timer = setTimeout(() => {
        pendingStateCaptures.delete(component);
        run();
    }, STATE_CAPTURE_DELAY_MS);

    pendingStateCaptures.set(component, { timer, run });
};

/**
 * Subscribes to the grid's layout-change events and buffers a page-level save
 * (debounced) whenever the user reorders, resizes, sorts, groups, filters,
 * searches or pages.
 *
 * The handler is wired AFTER initialization, so column auto-generation on load
 * does not trigger a save. Only genuine, post-load layout changes are persisted
 * — the save API is never called just because a page loaded.
 */
const wireAutoSave = (component: any, identity: GridIdentity): void => {
    // Only start persisting once the grid has finished its initial render and a
    // short settle window has passed. The `columns` optionChanged events
    // DevExtreme emits during load (auto widths, column generation) are not user
    // actions; persisting them would fire a POST on page load.
    const markReady = () => saveReadyGrids.add(component);
    const onFirstContentReady = () => {
        component.off("contentReady", onFirstContentReady);
        setTimeout(markReady, SAVE_READY_DELAY_MS);
    };
    component.on("contentReady", onFirstContentReady);
    // Fallback in case contentReady already fired before this subscription.
    setTimeout(markReady, SAVE_READY_FALLBACK_MS);

    component.on("optionChanged", (args: any) => {
        // Ignore changes produced by our own state() restore.
        if (applyingGridInstances.has(component)) {
            return;
        }

        // The whole `columns` array was replaced by the host (never a user
        // action). DevExtreme has just discarded the restored layout, so put it
        // back. Without this, a layout restored asynchronously from the DB (no
        // local cache, e.g. after clearing site data) is wiped by the next
        // React re-render of the grid's columns.
        if ((args?.fullName || args?.name) === "columns") {
            reapplyDesiredStateAfterColumnsReset(component);
            return;
        }

        // Ignore the load-time render/auto-width churn before the settle window.
        if (!saveReadyGrids.has(component)) {
            return;
        }

        const changedOption = String(args?.fullName || args?.name || "");
        if (!LAYOUT_OPTION_PATTERN.test(changedOption)) {
            return;
        }

        // Never persist before the DB layout has been applied, nor changes not
        // caused by the user (data refresh / auto-width churn).
        if (!isUserDrivenChange(component)) {
            return;
        }

        // Read the state only after DevExtreme has finished this update;
        // reading it here would return the layout from BEFORE the change.
        scheduleStateCapture(component, identity, changedOption);
    });
};

/**
 * Configures a single grid instance for automatic persistence.
 *
 * Runs deferred (outside onInitialized): mutating a grid during onInitialized
 * is a DevExtreme anti-pattern that can prevent sibling grids on the same page
 * from rendering. Deferring lets every grid finish its initial render first.
 */
const configureGridPersistence = (component: any): void => {
    try {
        // Skip if the grid was disposed before the deferred callback ran.
        if (!component || typeof component.option !== "function" || !component.element?.()) {
            return;
        }

        // Resolve identity BEFORE disabling stateStoring (reads its storageKey).
        const identity = resolveGridIdentity(component);
        debugLog("identity", { pageKey: identity.pageKey, gridKey: identity.gridKey });

        // Take full control of persistence: disable any native stateStoring so
        // there is a single source of truth (our API + per-user cache).
        // DevExtreme's native state load runs in a timer that is re-scheduled
        // whenever the dataSource changes, and it calls customLoad regardless of
        // `enabled`. Replace the legacy (cookie/localStorage) load/save with
        // no-ops so it can never overwrite the DB layout after we apply it.
        try {
            component.option("stateStoring", {
                enabled: false,
                type: "custom",
                customLoad: () => null,
                customSave: () => undefined,
            });
        } catch {
            // Some grids may not expose stateStoring; ignore.
        }

        trackGridLifetime(component, identity.pageKey);

        // Restore from the per-grid cache immediately for instant layout.
        applyGridState(component, readLocalCache(identity.localCacheKey));

        // Reconcile with the backend using the single, shared per-page GET.
        // The DB is the source of truth; the local cache is only a fast path.
        loadPageLayouts(identity.pageKey)
            .then((layoutByGridKey) => {
                const serverState = layoutByGridKey.get(identity.gridKey);
                debugLog(`reconcile ${identity.gridKey}`, { foundOnServer: isMeaningfulState(serverState) });
                if (!isMeaningfulState(serverState) || !component.element?.()) {
                    return;
                }

                const cachedRaw = JSON.stringify(readLocalCache(identity.localCacheKey) ?? {});
                const serverRaw = JSON.stringify(serverState);

                writeLocalCache(identity.localCacheKey, serverState);

                if (serverRaw !== cachedRaw) {
                    applyGridState(component, serverState);
                }
            })
            .catch(() => {
                // Keep the cached/default layout on failure.
            })
            .finally(() => {
                // Defer past the synchronous optionChanged churn from state().
                setTimeout(() => markGridReconciled(component), 0);
            });

        // Start saving on every genuine layout change.
        wireAutoSave(component, identity);
    } catch {
        // Never let one grid's persistence setup break the page.
    }
};

/**
 * Enables automatic grid layout persistence for every DataGrid in the app,
 * backed by the durable grid-layout API with a per-user offline cache.
 *
 * API calls are batched per page: one GET loads all grids on a page, and one
 * debounced POST saves all changed grids together. The save API is only called
 * when a layout actually changes — never on page load.
 *
 * Security note: only grid layout metadata (column order/width/visibility,
 * sorting, filtering, grouping, paging) is stored, scoped per user; no business
 * data is transmitted.
 */
export const initializeGlobalGridLayoutPersistence = (): void => {
    if (hasInitializedGridLayoutPersistence) {
        return;
    }

    hasInitializedGridLayoutPersistence = true;

    // Track genuine user interaction (capture phase so grid handlers that stop
    // propagation are still observed; passive to avoid blocking scrolling).
    if (typeof document !== "undefined") {
        const recordUserInput = () => {
            lastUserInputAt = Date.now();
        };
        ["pointerdown", "pointerup", "keydown"].forEach((eventName) =>
            document.addEventListener(eventName, recordUserInput, { capture: true, passive: true })
        );
    }

    dxDataGrid.defaultOptions({
        options: {
            onInitialized: (e: any) => {
                const component = e?.component;

                if (!component || typeof component.option !== "function") {
                    return;
                }

                if (configuredGridInstances.has(component)) {
                    return;
                }

                configuredGridInstances.add(component);

                // Defer out of the synchronous init/render pass so multiple grids
                // on the same page all render before any option mutation happens.
                setTimeout(() => configureGridPersistence(component), 0);
            },
        },
    });
};
